/* eslint-disable import/no-internal-modules */
import type { ApolloQueryResult } from '@apollo/client';
import { gql, useQuery } from '@apollo/client';
import { Exception } from '@livecontrol/core-ui';
import type { Asset } from '@livecontrol/scheduler/model';
import type { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { normalize as normalizeEvent, StandardEventResponse } from '../event/dbx';
import type { QueryResult } from '../graphql';
import * as Dbx from './dbx';

interface TVariables {
  input: {
    limit?: number;
    page?: number;
    client_id?: number;
    date_gte?: DateTime;
    date_lte?: DateTime;
    origin?: string;
    location_id?: string;
    sort_field?: string;
    sort_order?: string;
    wildcard?: string;
  };
}

interface TData {
  getAssetsPaginated: {
    assets?: (Dbx.Record & { event?: Dbx.Record })[];
    page?: number;
    has_more?: boolean;
  };
}

export const QUERY = gql`
  query GetAssetsPaginated($input: GetAssetsPaginatedInput) {
    getAssetsPaginated(input: $input) {
      error
      has_more
      page
      message
      assets {
        ...AssetPaginatedResponse
        event {
          ...StandardEventResponse
        }
      }
    }
  }

  ${Dbx.AssetPaginatedResponse}
  ${StandardEventResponse}
`;

interface Response {
  refetch?: (variables?: Partial<TVariables> | undefined) => Promise<ApolloQueryResult<TData>>;
  hasMore?: boolean;
  page?: number;
}

export const useAssetsPaginated = (
  args: TVariables
): QueryResult<Asset.withEvent[], 'assets'> & Response => {
  const [error, setError] = useState<Error | undefined>();
  const [assets, setAssets] = useState<Asset.withEvent[] | undefined>();
  const [hasMore, setHasMore] = useState<boolean | undefined>();
  const [page, setPage] = useState<number | undefined>();

  // Prepare the query
  const { loading, data, refetch } = useQuery<TData, TVariables>(QUERY, {
    variables: args,
    onError() {
      setError(Exception.KABOOM);
    }
  });

  // When available, parse server response
  useEffect(() => {
    let assets_;
    let error_;
    const paginatedAssets = data?.getAssetsPaginated.assets;

    if (paginatedAssets) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      assets_ = paginatedAssets.map<Asset.withEvent | undefined>((record) => {
        const asset = Dbx.normalize(record);

        if (!asset) {
          return undefined;
        }

        const event = normalizeEvent(record.event);

        return { ...asset, eventData: event };
      }).filter((asset): asset is Asset.withEvent => !!asset);
    }

    setHasMore(data?.getAssetsPaginated.has_more ?? false);

    if (data?.getAssetsPaginated.page) {
      setPage(data.getAssetsPaginated.page);
    }

    setAssets(assets_);

    setError(error_);
  }, [data]);

  return { loading, error, assets, refetch, page, hasMore };
};
